/**
 * Singleton class for managing cache and exposing modular API interfaces
 * @copyright 2025-2026 Soter Technologies, LLC. All rights reserved.
 * @file index.js
 * @author Bryan Lai
 */

import AdminCache from './pages/admin'

class CacheManager {
    constructor() {
        /**
         * Map to store cached values by key
         * @type {Map<string, { data: any, expiry: number }>}
         */
        this.cache = new Map()

        /**
         * Modular API cache interfaces
         * Example: this.pages.admin provides admin-related cache methods
         */
        this.pages = {
            admin: new AdminCache(this),
        }
    }

    /**
     * Set a cache key with value and TTL (time to live)
     * @param {string} key - Unique cache key
     * @param {*} value - Value to cache (e.g. {PageNumber: number, Results: Array<any>, ReturnedResults: number, TotalPages: number, TotalResults: number, LastUpdated: number})
     * @param {number} ttlMs - Time to live in milliseconds
     */
    set(key, value, ttlMs) {
        this.cache.set(key, { data: value, expiry: Date.now() + ttlMs })
    }

    /**
     * Returns value for a key if it exists and is not expired
     * @param {string} key - Cache key to retrieve
     * @returns {*} Returned object or null if not found/expired {data: <data>, expiry: <Date>}
     */
    get(key) {
        // Grabs entry from the cache
        const entry = this.cache.get(key)
        // If an entry with the given key does not exist in the cache OR it does exist but has expired, delete it from the cache and return null. 
        if (!entry || Date.now() > entry.expiry) {
            this.cache.delete(key)
            return null
        }
        return entry
    }

    /**
     * Deletes cache for a specific key
     * @param {string} key - Cache key to clear
     */
    delete(key) {
        this.cache.delete(key)
    }
}

export default CacheManager